import { AccessTime, InfoOutlined } from "@mui/icons-material";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import { Box, Card, Typography, Chip, Tooltip } from "@mui/material";
import moment from "moment";

export default function LastCheckin(props) {
  const { title, value, packInfo } = props;
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  // Calculer le temps écoulé et le statut
  const getTimeInfo = (date) => {
    if (date === "N/A" || !date || date === "" || !moment(date).isValid()) {
      return { text: "Aucune donnée", status: "error", color: "#ef4444" };
    }

    const now = moment();
    const lastUpdate = moment(date);
    const diffMinutes = now.diff(lastUpdate, 'minutes');
    const diffHours = now.diff(lastUpdate, 'hours');
    const diffDays = now.diff(lastUpdate, 'days');

    let text, status, color;

    // Déterminer le texte
    if (diffMinutes < 1) {
      text = "À l'instant";
    } else if (diffMinutes < 60) {
      text = `Il y a ${diffMinutes} min`;
    } else if (diffHours < 24) {
      text = `Il y a ${diffHours}h${diffMinutes % 60 > 0 ? ` ${diffMinutes % 60}min` : ''}`;
    } else {
      text = `Il y a ${diffDays}j`;
    }

    // Déterminer le statut et la couleur
    if (diffMinutes <= 60) {
      status = "online";
      color = "#22c55e"; // Vert : vu dans l'heure
    } else if (diffHours <= 2) {
      status = "warning";
      color = "#f59e0b"; // Orange : entre 1h et 2h
    } else {
      status = "offline";
      color = "#ef4444"; // Rouge : plus de 2h
    }

    return { text, status, color, fullDate: moment(date).format("DD/MM HH:mm") };
  };

  const timeInfo = getTimeInfo(value);

  // Couleur du liseré selon le type d'appareil
  const getBorderColor = () => {
    const titleLower = title?.toLowerCase() || "";
    if (titleLower.includes("eastron") || titleLower.includes("electric")) return "var(--color-electricity)";
    if (titleLower.includes("cubic") || titleLower.includes("water")) return "var(--color-water)";
    if (titleLower.includes("temp") || titleLower.includes("température")) return "var(--color-temperature)";
    return "var(--text-muted)";
  };

  const tooltipContent = (
    <Box sx={{ p: 0.5 }}>
      <Typography sx={{ fontWeight: 600, fontSize: "0.85rem", mb: 0.5 }}>
        État de connexion
      </Typography>
      <Typography sx={{ fontSize: "0.75rem", color: "rgba(255,255,255,0.75)", mb: 0.75 }}>
        Temps écoulé depuis la dernière donnée reçue.
      </Typography>
      {[
        { c: "#22c55e", range: "moins d'1h" },
        { c: "#f59e0b", range: "entre 1h et 2h" },
        { c: "#ef4444", range: "plus de 2h" },
      ].map((row) => (
        <Box key={row.c} sx={{ display: "flex", alignItems: "center", gap: 0.75, my: 0.25 }}>
          <Box sx={{
            width: 10,
            height: 10,
            borderRadius: "50%",
            backgroundColor: row.c,
            boxShadow: `0 0 6px ${row.c}`,
            flexShrink: 0,
          }} />
          <Typography sx={{ fontSize: "0.78rem", color: "rgba(255,255,255,0.9)" }}>
            {row.range}
          </Typography>
        </Box>
      ))}
    </Box>
  );

  return (
    <Card sx={{
      display: "flex",
      flexDirection: "row",
      alignItems: "center",
      justifyContent: "space-between",
      position: "relative",
      backgroundColor: "var(--bg-card)",
      p: isMobile ? 1 : 1.5,
      minHeight: isMobile ? 55 : 65,
      borderRadius: "6px !important",
      border: "1px solid var(--border-subtle)",
      boxShadow: "var(--shadow-md)",
    }}>
      {/* Liseré coloré en bas */}
      <Box sx={{
        position: "absolute",
        bottom: 0,
        left: 0,
        right: 0,
        height: "2px",
        backgroundColor: getBorderColor(),
      }} />

      {/* Indicateur de statut (point coloré) */}
      <Box sx={{
        display: "flex",
        alignItems: "center",
        gap: 1,
        flex: 1,
      }}>
        <Box sx={{
          width: 10,
          height: 10,
          borderRadius: "50%",
          backgroundColor: timeInfo.color,
          boxShadow: `0 0 8px ${timeInfo.color}`,
          animation: timeInfo.status === "online" ? "pulse 2s infinite" : "none",
          "@keyframes pulse": {
            "0%": { opacity: 1, transform: "scale(1)" },
            "50%": { opacity: 0.6, transform: "scale(1.1)" },
            "100%": { opacity: 1, transform: "scale(1)" },
          },
        }} />

        <Box>
          <Typography sx={{
            fontSize: isMobile ? "0.85rem" : "0.95rem",
            fontWeight: 500,
            lineHeight: 1.2,
          }}>
            {title}
          </Typography>
          {packInfo && (
            <Chip
              label={`Pack: ${packInfo}`}
              size="small"
              sx={{
                height: 18,
                fontSize: "0.7rem",
                backgroundColor: "#26A69A",
                color: "black"
              }}
            />
          )}
        </Box>
      </Box>

      {/* Temps et icône */}
      <Box sx={{
        display: "flex",
        alignItems: "center",
        gap: 1,
      }}>
        <Box sx={{ textAlign: "right" }}>
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 0.5 }}>
            <Typography sx={{
              fontSize: isMobile ? "0.95rem" : "1.1rem",
              fontWeight: 600,
              color: timeInfo.color,
              lineHeight: 1.2,
            }}>
              {timeInfo.text}
            </Typography>
            <Tooltip title={tooltipContent} arrow placement="top" enterTouchDelay={0} leaveTouchDelay={4000}>
              <InfoOutlined sx={{
                fontSize: isMobile ? 14 : 16,
                color: "var(--text-muted)",
                cursor: "help",
                "&:hover": { color: timeInfo.color },
              }} />
            </Tooltip>
          </Box>
          {timeInfo.fullDate && (
            <Typography sx={{
              fontSize: isMobile ? "0.7rem" : "0.75rem",
              color: "var(--text-muted)",
              lineHeight: 1,
            }}>
              {timeInfo.fullDate}
            </Typography>
          )}
        </Box>
        <AccessTime sx={{
          fontSize: isMobile ? 24 : 28,
          color: timeInfo.color,
          opacity: 0.8,
        }} />
      </Box>
    </Card>
  );
}
